import request from 'utilities/request'
import { dispatchCustomEvent } from 'utilities/events'
import validate from './validation-rules'

const classes = {
    HIDDEN: 'u-d-none',
}

export default function Form(form) {
    const btnSend = form.querySelector('.js-form-btn-send')
    const btnBack = form.querySelector('.js-form-btn-back')
    const formInputs = form.querySelectorAll('.form__input')
    const formCheckbox = form.querySelector('#form-checkbox')
    const confirmMsg = form.querySelector('.js-confirm-msg')
    const formLang = form.querySelector('#form-lang')

    const state = {} // Will contain all form inputs with their validation rules and messages

    btnBack.addEventListener('click', handleBtnBackClick)
    form.addEventListener('submit', handleFormSubmit)

    setInitialState()

    /**
     * Set the initial state with all inputs
     */
    function setInitialState() {
        formInputs.forEach(item => {
            // Add input name as key to state
            state[item.id] = {
                // Add input value
                value: item.value,

                // Add input validation rules
                rules: {
                    maxLength: item.getAttribute('max-length'),
                    regularExpression: item.getAttribute('regular-expression'),
                    required: item.hasAttribute('required'),
                },

                // Add input validation messages
                messages: {
                    maxLength: item.getAttribute('max-length-warning'),
                    regularExpression: item.getAttribute(
                        'regular-expression-warning'
                    ),
                    required: item.getAttribute('required-warning'),
                },
            }
        })
    }

    /**
     * Set state with new input values
     */
    function setState() {
        formInputs.forEach(item => {
            state[item.id].value = item.value
        })
    }

    function handleFormSubmit(e) {
        e.preventDefault() // Do not send the HTML form

        if (isFormValid()) sendForm()
    }

    function handleBtnBackClick() {
        setBtnState('enabled')
        resetFormData()
        handleFormPhaseChange('start')
    }

    /**
     * Switch the form phase to start or end
     *
     * @param {String} phase - What phase to switch form to
     */
    function handleFormPhaseChange(phase) {
        let classAction = ''

        switch (phase) {
            case 'start':
                classAction = 'remove'
                break
            case 'end':
                classAction = 'add'
                break
            default:
                return
        }

        form.classList[classAction]('contact--form-sent')
    }

    /**
     * Push the sent form event to the dataLayer
     */
    function handleFormTracking() {
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'form_sent',
            })
        }
    }

    /**
     * Set the button state to enabled or disabled
     *
     * @param {String} state - What state set button to
     */
    function setBtnState(state) {
        let classAction = ''
        let disabled

        switch (state) {
            case 'disabled':
                classAction = 'add'
                disabled = true
                break
            case 'enabled':
                classAction = 'remove'
                disabled = false
                break
            default:
                return
        }

        btnSend.classList[classAction]('btn--is-loading')
        btnSend.disabled = disabled
    }

    /**
     * Check if all form fields are valid
     *
     * @returns {Boolean} - Form is valid
     */
    function isFormValid() {
        const invalidFields = {} // Will contain invalid field names with validation messages
        const validFields = [] // Will contain valid fields

        setState()

        // 1. Check all fields and validate them
        Object.entries(state).forEach(
            ([fieldName, { messages, rules, value }]) => {
                // Loop validation rules of each field
                Object.entries(rules).forEach(([ruleName, ruleValue]) => {
                    if (ruleValue) {
                        // Validation rule is present, validate the field
                        if (!validate[ruleName](value, ruleValue)) {
                            // Field is invalid, add its name and message to invalidFields
                            invalidFields[fieldName] = messages[ruleName]
                        }
                    }
                })

                if (invalidFields[fieldName] === undefined) {
                    // Field is valid, add its name to validFields
                    validFields.push(fieldName)
                }
            }
        )

        // 2. Check all invalid fields and reflect them in UI
        Object.entries(invalidFields).forEach(([fieldName, errorMessage]) => {
            // Get the DOM element
            const invalidInput = [...formInputs].find(
                input => input.id === fieldName
            )

            // Display the error message and apply error styling to the field
            invalidInput.parentNode.classList.add('form__field--has-error')
            invalidInput.nextElementSibling.textContent = errorMessage
        })

        // 2. Check all valid fields and reflect them in UI
        validFields.forEach(fieldName => {
            // Get the DOM element
            const validInput = [...formInputs].find(
                input => input.id === fieldName
            )

            // Hide the error message and remove error styling from the field
            validInput.parentNode.classList.remove('form__field--has-error')
            validInput.nextElementSibling.textContent = ''
        })

        dispatchCustomEvent('formValidated')

        return !Object.keys(invalidFields).length
    }

    /**
     * Prepare data for the request
     *
     * @returns {Object} requestData
     */
    function getRequestData() {
        const requestData = {} // Will contain all inputs with their values

        formInputs.forEach(input => {
            // Assign name and value of input fields to requestData
            requestData[input.id] = input.value
        })

        // Assign name and value of checkbox field to requestData
        requestData[formCheckbox.id] = formCheckbox.checked

        // Assign name and value of language input to requestData
        requestData[formLang.id] = formLang.value

        return requestData
    }

    /**
     * Prepare form to send request
     */
    function sendForm() {
        const { serviceUrl } = form.dataset
        let data = getRequestData()

        setBtnState('disabled')

        // Do not change the form phase immediately, keep some time for the animation
        setTimeout(() => {
            request({ data, url: serviceUrl })
                .then(() => {
                    handleFormTracking()
                    handleFormPhaseChange('end')
                    confirmMsg.classList.add(classes.HIDDEN)

                    if (data['form-checkbox']) {
                        confirmMsg.classList.remove(classes.HIDDEN)
                    }
                })
                .catch(error => {
                    console.error(error)
                })
        }, 500)
    }

    /**
     * Return the form to its initial state with all inputs cleared and button enabled
     */
    function resetFormData() {
        formInputs.forEach(input => (input.value = ''))
        formCheckbox.checked = false
    }
}
